import React, { useState, useMemo } from "react";
import {
  FiPlus, FiSearch, FiEdit2, FiTrash2, FiX, FiMapPin, FiCheck,
  FiAlertTriangle, FiCpu, FiUsers, FiActivity, FiSettings,
  FiList, FiGrid, FiChevronLeft, FiPower, FiClipboard,
  FiFileText, FiBell, FiChevronRight, FiFilter,
} from "react-icons/fi";
import { MapContainer, TileLayer, Marker, Popup } from "react-leaflet";
import L from "leaflet";
import "leaflet/dist/leaflet.css";
import { siteDummyData, SOLUTION_META } from "../../Data/SiteData";
import "./SiteManagement.css";

// ── Constants ─────────────────────────────────────────────────────────────────
const SITE_TYPES    = ["Factory", "Apartment", "Commercial", "Farm", "Hospital", "School", "Warehouse"];
const SITE_PLANS    = ["Basic", "Professional", "Enterprise"];
const SITE_STATUSES = ["Active", "Inactive", "Trial"];
const ALL_SOLUTIONS = ["STP", "AQM", "Irrigation", "Water", "Facility", "Fleet"];
const USER_ROLES    = ["Site Admin", "Facility Manager", "Technician", "Viewer"];
const TIMEZONES     = ["IST (UTC+5:30)", "UTC", "CST (UTC+8)"];

// ── Helper functions ───────────────────────────────────────────────────────────
const healthColor = (s) => s >= 85 ? "#10b981" : s >= 65 ? "#f59e0b" : s > 0 ? "#ef4444" : "#94a3b8";
const statusBg    = { Active: { bg: "#d1fae5", color: "#065f46" }, Inactive: { bg: "#fee2e2", color: "#991b1b" }, Trial: { bg: "#fef3c7", color: "#92400e" } };
const sevColor    = { Critical: "#ef4444", Warning: "#f59e0b", Info: "#3b82f6" };

const StatusBadge = ({ status }) => (
  <span className="sm-badge" style={{ background: statusBg[status]?.bg || "#f1f5f9", color: statusBg[status]?.color || "#475569" }}>
    {status}
  </span>
);

const SolutionChip = ({ sol }) => {
  const m = SOLUTION_META[sol] || { color: "#6b7280", bg: "#f3f4f6" };
  return <span className="sm-sol-chip" style={{ background: m.bg, color: m.color }}>{sol}</span>;
};

const HealthRing = ({ score }) => (
  <span className="sm-health-val" style={{ color: healthColor(score) }}>{score > 0 ? score : "—"}</span>
);

// ── Leaflet site icon ─────────────────────────────────────────────────────────
function makeSiteIcon(site, isSelected) {
  const col = healthColor(site.healthScore);
  const size = isSelected ? 38 : 30;
  return L.divIcon({
    className: "",
    html: `<div style="
      width:${size}px;height:${size}px;background:${col};
      border:${isSelected ? 3 : 2}px solid ${isSelected ? "#1e293b" : "white"};
      border-radius:50%;display:flex;align-items:center;justify-content:center;
      color:white;font-size:${Math.round(size * 0.38)}px;font-weight:700;
      box-shadow:0 2px 6px rgba(0,0,0,0.3);">S</div>`,
    iconSize: [size, size], iconAnchor: [size / 2, size / 2], popupAnchor: [0, -(size / 2 + 4)],
  });
}

// ── Delete Confirmation Dialog ────────────────────────────────────────────────
const DeleteDialog = ({ site, onClose, onConfirm }) => (
  <div className="sm-overlay">
    <div className="sm-delete-dialog">
      <div className="sm-delete-icon"><FiAlertTriangle size={28} color="#ef4444" /></div>
      <div className="sm-delete-title">Deactivate / Delete Site?</div>
      <div className="sm-delete-body">
        <strong>{site.name}</strong> has <strong>{site.deviceCount} devices</strong> and <strong>{site.userCount} users</strong> assigned.
        <br /><br />
        Choose an action:
      </div>
      <div className="sm-delete-actions">
        <button className="sm-btn sm-btn-amber" onClick={() => onConfirm("deactivate")}>
          <FiPower size={14} /> Deactivate Site
        </button>
        <button className="sm-btn sm-btn-red"
          onClick={() => onConfirm("delete")}
          disabled={site.deviceCount > 0}
          title={site.deviceCount > 0 ? "Remove all devices before hard delete" : ""}
          style={{ opacity: site.deviceCount > 0 ? 0.4 : 1 }}>
          <FiTrash2 size={14} /> Hard Delete
        </button>
        <button className="sm-btn sm-btn-ghost" onClick={onClose}>Cancel</button>
      </div>
      {site.deviceCount > 0 && (
        <div className="sm-delete-note">⚠ Hard delete requires removing all devices first.</div>
      )}
    </div>
  </div>
);

// ── Create / Edit Site Modal ──────────────────────────────────────────────────
const MODAL_SECTIONS = [
  { id: "basic",     label: "Basic Info",   icon: FiClipboard },
  { id: "solutions", label: "Solutions",    icon: FiGrid      },
  { id: "devices",   label: "Devices",      icon: FiCpu       },
  { id: "users",     label: "Users",        icon: FiUsers     },
  { id: "advanced",  label: "Advanced",     icon: FiSettings  },
];

const CreateSiteModal = ({ editSite, onClose }) => {
  const [section, setSection] = useState("basic");
  const [form, setForm] = useState({
    name: editSite?.name || "",
    org: editSite?.org || "",
    type: editSite?.type || "Factory",
    address: editSite?.address || "",
    lat: editSite?.lat || "",
    lng: editSite?.lng || "",
    timezone: editSite?.timezone || "IST (UTC+5:30)",
    contact: editSite?.contact || "",
    phone: editSite?.phone || "",
    email: editSite?.email || "",
    status: editSite?.status || "Active",
    plan: editSite?.plan || "Professional",
  });
  const [selSolutions, setSelSolutions] = useState(
    editSite ? editSite.solutions.map(s => s.id) : ["Water"]
  );
  const [selDevices, setSelDevices] = useState([]);
  const [selUsers, setSelUsers]     = useState([]);
  const [devSearch, setDevSearch]   = useState("");
  const [userSearch, setUserSearch] = useState("");
  const [adv, setAdv] = useState({
    escalation: editSite?.settings.escalation || "1h → Site Admin → Org Admin",
    maintenanceWindow: editSite?.settings.maintenanceWindow || "Sunday 02:00–04:00 IST",
    slaTarget: editSite?.settings.slaTarget || "99.0%",
    dataRetention: editSite?.settings.dataRetention || "12 months",
  });

  const sf = (k, v) => setForm(p => ({ ...p, [k]: v }));
  const toggleSol = (id) => setSelSolutions(p => p.includes(id) ? p.filter(x => x !== id) : [...p, id]);
  const toggleDev = (id) => setSelDevices(p => p.includes(id) ? p.filter(x => x !== id) : [...p, id]);
  const toggleUsr = (id) => setSelUsers(p => p.includes(id) ? p.filter(x => x !== id) : [...p, id]);

  const filteredDevices = siteDummyData.devicePool.filter(d =>
    !devSearch || d.name.toLowerCase().includes(devSearch.toLowerCase()) || d.id.toLowerCase().includes(devSearch.toLowerCase())
  );
  const filteredUsers = siteDummyData.userPool.filter(u =>
    !userSearch || u.name.toLowerCase().includes(userSearch.toLowerCase()) || u.email.toLowerCase().includes(userSearch.toLowerCase())
  );

  return (
    <div className="sm-overlay">
      <div className="sm-modal">
        {/* Header */}
        <div className="sm-modal-header">
          <span>{editSite ? `Edit Site — ${editSite.name}` : "Create New Site"}</span>
          <button className="sm-icon-btn" onClick={onClose}><FiX size={18} /></button>
        </div>

        <div className="sm-modal-body">
          {/* Left Section Nav */}
          <div className="sm-modal-nav">
            {MODAL_SECTIONS.map((s, i) => (
              <button
                key={s.id}
                className={`sm-modal-nav-btn ${section === s.id ? "sm-modal-nav-active" : ""}`}
                onClick={() => setSection(s.id)}
              >
                <s.icon size={15} />
                <span>{s.label}</span>
                <span className="sm-modal-nav-num">{i + 1}</span>
              </button>
            ))}
          </div>

          {/* Right Content */}
          <div className="sm-modal-content">

            {/* ── A: Basic Info ── */}
            {section === "basic" && (
              <div className="sm-form-section">
                <div className="sm-form-title">A. Basic Information</div>
                {[
                  { label: "Site Name *",     key: "name",    type: "input",  placeholder: "e.g. Mumbai Factory – Block A" },
                  { label: "Organization",    key: "org",     type: "input",  placeholder: "e.g. Tata Industries Ltd." },
                  { label: "Address",         key: "address", type: "input",  placeholder: "Full address" },
                  { label: "GPS Latitude",    key: "lat",     type: "input",  placeholder: "e.g. 19.0760" },
                  { label: "GPS Longitude",   key: "lng",     type: "input",  placeholder: "e.g. 72.8777" },
                  { label: "Contact Person",  key: "contact", type: "input",  placeholder: "e.g. Rajesh Mehta" },
                  { label: "Phone",           key: "phone",   type: "input",  placeholder: "+91-XXXXX-XXXXX" },
                  { label: "Email",           key: "email",   type: "input",  placeholder: "site@company.com" },
                ].map(f => (
                  <div key={f.key} className="sm-row">
                    <label className="sm-lbl">{f.label}</label>
                    <input className="sm-inp" type="text" value={form[f.key]} onChange={e => sf(f.key, e.target.value)} placeholder={f.placeholder} />
                  </div>
                ))}
                {[
                  { label: "Site Type",  key: "type",     opts: SITE_TYPES    },
                  { label: "Status",     key: "status",   opts: SITE_STATUSES },
                  { label: "Plan",       key: "plan",     opts: SITE_PLANS    },
                  { label: "Timezone",   key: "timezone", opts: TIMEZONES     },
                ].map(f => (
                  <div key={f.key} className="sm-row">
                    <label className="sm-lbl">{f.label}</label>
                    <select className="sm-inp" value={form[f.key]} onChange={e => sf(f.key, e.target.value)}>
                      {f.opts.map(o => <option key={o}>{o}</option>)}
                    </select>
                  </div>
                ))}
              </div>
            )}

            {/* ── B: Solutions ── */}
            {section === "solutions" && (
              <div className="sm-form-section">
                <div className="sm-form-title">B. Solution Assignment</div>
                <p className="sm-form-hint">Enable the IoT solutions for this site.</p>
                <div className="sm-sol-grid">
                  {ALL_SOLUTIONS.map(sol => {
                    const m = SOLUTION_META[sol] || {};
                    const on = selSolutions.includes(sol);
                    return (
                      <div key={sol} className={`sm-sol-card ${on ? "sm-sol-on" : ""}`} onClick={() => toggleSol(sol)} style={{ borderColor: on ? m.color : "#e5e7eb" }}>
                        <div className="sm-sol-card-header" style={{ color: m.color }}>
                          <span className="sm-sol-card-name">{sol}</span>
                          <div className={`sm-toggle ${on ? "sm-toggle-on" : ""}`} style={{ background: on ? m.color : "#d1d5db" }}>
                            <div className="sm-toggle-thumb" />
                          </div>
                        </div>
                        <div className="sm-sol-card-desc" style={{ color: "#6b7280", fontSize: 12 }}>
                          {sol === "STP"        && "Sewage Treatment Plant monitoring & control"}
                          {sol === "AQM"        && "Air Quality Monitoring – PM, CO2, VOC"}
                          {sol === "Irrigation" && "Smart irrigation with soil & weather sensors"}
                          {sol === "Water"      && "Water tank, pump & consumption management"}
                          {sol === "Facility"   && "HVAC, energy, occupancy & security"}
                          {sol === "Fleet"      && "Vehicle tracking, fuel & driver analytics"}
                        </div>
                        {on && (
                          <div className="sm-sol-plan-row">
                            <label style={{ fontSize: 11, color: "#6b7280" }}>Subscription Plan</label>
                            <select className="sm-inp-sm" onClick={e => e.stopPropagation()}>
                              {SITE_PLANS.map(p => <option key={p}>{p}</option>)}
                            </select>
                          </div>
                        )}
                      </div>
                    );
                  })}
                </div>
                <div className="sm-sel-count">{selSolutions.length} solution{selSolutions.length !== 1 ? "s" : ""} selected</div>
              </div>
            )}

            {/* ── C: Devices ── */}
            {section === "devices" && (
              <div className="sm-form-section">
                <div className="sm-form-title">C. Device Assignment</div>
                <p className="sm-form-hint">Assign existing unallocated devices to this site.</p>
                <div className="sm-search-wrap">
                  <FiSearch size={14} color="#9ca3af" />
                  <input className="sm-search" placeholder="Search devices…" value={devSearch} onChange={e => setDevSearch(e.target.value)} />
                </div>
                <div className="sm-assign-list">
                  {filteredDevices.map(d => {
                    const on = selDevices.includes(d.id);
                    return (
                      <div key={d.id} className={`sm-assign-row ${on ? "sm-assign-on" : ""}`} onClick={() => toggleDev(d.id)}>
                        <input type="checkbox" checked={on} readOnly />
                        <div style={{ flex: 1 }}>
                          <div style={{ fontWeight: 700, fontSize: 13 }}>{d.name}</div>
                          <div style={{ fontSize: 11, color: "#9ca3af" }}>{d.id} · {d.type} · {d.connectivity}</div>
                        </div>
                        <span className="sm-badge" style={{ background: "#d1fae5", color: "#065f46" }}>{d.status}</span>
                      </div>
                    );
                  })}
                </div>
                <div className="sm-sel-count">{selDevices.length} device{selDevices.length !== 1 ? "s" : ""} selected</div>
              </div>
            )}

            {/* ── D: Users ── */}
            {section === "users" && (
              <div className="sm-form-section">
                <div className="sm-form-title">D. User Assignment</div>
                <p className="sm-form-hint">Assign platform users and their roles for this site.</p>
                <div className="sm-search-wrap">
                  <FiSearch size={14} color="#9ca3af" />
                  <input className="sm-search" placeholder="Search users…" value={userSearch} onChange={e => setUserSearch(e.target.value)} />
                </div>
                <div className="sm-assign-list">
                  {filteredUsers.map(u => {
                    const on = selUsers.includes(u.id);
                    return (
                      <div key={u.id} className={`sm-assign-row ${on ? "sm-assign-on" : ""}`} onClick={() => toggleUsr(u.id)}>
                        <input type="checkbox" checked={on} readOnly />
                        <div style={{ flex: 1 }}>
                          <div style={{ fontWeight: 700, fontSize: 13 }}>{u.name}</div>
                          <div style={{ fontSize: 11, color: "#9ca3af" }}>{u.email}</div>
                        </div>
                        <span className="sm-badge" style={{ background: "#ede9fe", color: "#5b21b6" }}>{u.role}</span>
                      </div>
                    );
                  })}
                </div>
                <div className="sm-sel-count">{selUsers.length} user{selUsers.length !== 1 ? "s" : ""} selected</div>
              </div>
            )}

            {/* ── E: Advanced ── */}
            {section === "advanced" && (
              <div className="sm-form-section">
                <div className="sm-form-title">E. Advanced Settings</div>
                {[
                  { label: "Alert Escalation Rule", key: "escalation",       placeholder: "e.g. 1h → Admin → 4h → Org Admin" },
                  { label: "Maintenance Window",     key: "maintenanceWindow", placeholder: "e.g. Sunday 02:00–04:00 IST" },
                  { label: "SLA Target (%)",         key: "slaTarget",         placeholder: "e.g. 99.5%" },
                  { label: "Data Retention",         key: "dataRetention",     placeholder: "e.g. 12 months" },
                ].map(f => (
                  <div key={f.key} className="sm-row">
                    <label className="sm-lbl">{f.label}</label>
                    <input className="sm-inp" type="text" value={adv[f.key]} onChange={e => setAdv(p => ({ ...p, [f.key]: e.target.value }))} placeholder={f.placeholder} />
                  </div>
                ))}
                <div className="sm-row">
                  <label className="sm-lbl">Notification Channels</label>
                  <div style={{ display: "flex", gap: 8, flexWrap: "wrap", marginTop: 4 }}>
                    {["SMS", "Email", "WhatsApp", "Push"].map(ch => (
                      <label key={ch} style={{ display: "flex", alignItems: "center", gap: 4, fontSize: 13, cursor: "pointer" }}>
                        <input type="checkbox" defaultChecked style={{ accentColor: "#7c3aed" }} />
                        {ch}
                      </label>
                    ))}
                  </div>
                </div>
              </div>
            )}
          </div>
        </div>

        {/* Footer */}
        <div className="sm-modal-footer">
          <div style={{ display: "flex", gap: 8 }}>
            {MODAL_SECTIONS.findIndex(s => s.id === section) > 0 && (
              <button className="sm-btn sm-btn-ghost" onClick={() => setSection(MODAL_SECTIONS[MODAL_SECTIONS.findIndex(s => s.id === section) - 1].id)}>
                ← Back
              </button>
            )}
          </div>
          <div style={{ display: "flex", gap: 8 }}>
            <button className="sm-btn sm-btn-ghost" onClick={onClose}>Cancel</button>
            {MODAL_SECTIONS.findIndex(s => s.id === section) < MODAL_SECTIONS.length - 1 ? (
              <button className="sm-btn sm-btn-primary" onClick={() => setSection(MODAL_SECTIONS[MODAL_SECTIONS.findIndex(s => s.id === section) + 1].id)}>
                Next →
              </button>
            ) : (
              <button className="sm-btn sm-btn-primary" onClick={onClose}>
                <FiCheck size={14} /> {editSite ? "Save Changes" : "Create Site"}
              </button>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

// ═══════════════════════════════════════════════════════════════════════════════
//  SITE LIST VIEW
// ═══════════════════════════════════════════════════════════════════════════════
const SiteListView = ({ onViewSite, onEdit, onDelete, onCreateSite }) => {
  const [search,    setSearch]    = useState("");
  const [typeF,     setTypeF]     = useState("All");
  const [statusF,   setStatusF]   = useState("All");
  const [solF,      setSolF]      = useState("All");
  const [viewMode,  setViewMode]  = useState("table"); // "table" | "map"
  const [mapSelected, setMapSelected] = useState(null);

  const sites = siteDummyData.sites;

  const filtered = useMemo(() => sites.filter(s => {
    if (search    && !s.name.toLowerCase().includes(search.toLowerCase()) && !s.org.toLowerCase().includes(search.toLowerCase())) return false;
    if (typeF   !== "All" && s.type   !== typeF)   return false;
    if (statusF !== "All" && s.status !== statusF) return false;
    if (solF    !== "All" && !s.solutions.some(so => so.id === solF)) return false;
    return true;
  }), [search, typeF, statusF, solF, sites]);

  const totalAlerts = sites.reduce((a, s) => a + s.alerts.length, 0);
  const activeCount = sites.filter(s => s.status === "Active").length;

  return (
    <div className="sm-container">
      {/* Page Header */}
      <div className="sm-page-header">
        <div>
          <div className="sm-page-title">Site Management</div>
          <div className="sm-page-sub">
            {sites.length} sites · {activeCount} active · {totalAlerts} active alerts
          </div>
        </div>
        <div className="sm-header-actions">
          <button className={`sm-btn sm-btn-ghost ${viewMode === "table" ? "sm-btn-view-active" : ""}`} onClick={() => setViewMode("table")}>
            <FiList size={14} /> Table
          </button>
          <button className={`sm-btn sm-btn-ghost ${viewMode === "map" ? "sm-btn-view-active" : ""}`} onClick={() => setViewMode("map")}>
            <FiMapPin size={14} /> Map
          </button>
          <button className="sm-btn sm-btn-primary" onClick={onCreateSite}>
            <FiPlus size={14} /> Create Site
          </button>
        </div>
      </div>

      {/* Filter Bar */}
      <div className="sm-filter-bar">
        <div className="sm-search-wrap">
          <FiSearch size={14} color="#9ca3af" />
          <input className="sm-search" placeholder="Search sites or organizations…" value={search} onChange={e => setSearch(e.target.value)} />
        </div>
        <div className="sm-filter-chip-row">
          <FiFilter size={13} color="#6b7280" />
          {["All", ...SITE_TYPES].map(t => (
            <button key={t} className={`sm-filter-chip ${typeF === t ? "sm-chip-active" : ""}`} onClick={() => setTypeF(t)}>{t}</button>
          ))}
        </div>
        <div className="sm-filter-right">
          <select className="sm-sel" value={statusF} onChange={e => setStatusF(e.target.value)}>
            <option value="All">All Status</option>
            {SITE_STATUSES.map(s => <option key={s}>{s}</option>)}
          </select>
          <select className="sm-sel" value={solF} onChange={e => setSolF(e.target.value)}>
            <option value="All">All Solutions</option>
            {ALL_SOLUTIONS.map(s => <option key={s}>{s}</option>)}
          </select>
        </div>
      </div>

      {/* ── TABLE VIEW ── */}
      {viewMode === "table" && (
        <div className="sm-table-wrap">
          <table className="sm-table">
            <thead>
              <tr>
                {["Site Name", "Type", "Location", "Solutions", "Devices", "Users", "Alerts", "Health", "Status", "Actions"].map(h => (
                  <th key={h}>{h}</th>
                ))}
              </tr>
            </thead>
            <tbody>
              {filtered.map(site => (
                <tr key={site.id} className="sm-table-row" onClick={() => onViewSite(site)}>
                  <td>
                    <div className="sm-site-name-cell">
                      <div className="sm-site-avatar" style={{ background: healthColor(site.healthScore) }}>
                        {site.name.charAt(0)}
                      </div>
                      <div>
                        <div style={{ fontWeight: 700, fontSize: 13 }}>{site.name}</div>
                        <div style={{ fontSize: 11, color: "#94a3b8" }}>{site.org}</div>
                      </div>
                    </div>
                  </td>
                  <td><span className="sm-type-chip">{site.type}</span></td>
                  <td style={{ fontSize: 12, color: "#475569" }}>{site.address.split(",").slice(-2).join(",").trim()}</td>
                  <td>
                    <div style={{ display: "flex", gap: 4, flexWrap: "wrap" }}>
                      {site.solutions.filter(s => s.enabled).map(s => <SolutionChip key={s.id} sol={s.id} />)}
                    </div>
                  </td>
                  <td style={{ textAlign: "center", fontWeight: 600 }}>{site.devices.length}</td>
                  <td style={{ textAlign: "center", fontWeight: 600 }}>{site.users.length}</td>
                  <td style={{ textAlign: "center" }}>
                    {site.alerts.length > 0
                      ? <span style={{ color: "#ef4444", fontWeight: 700 }}>{site.alerts.length}</span>
                      : <span style={{ color: "#10b981" }}>—</span>}
                  </td>
                  <td>
                    <span style={{ fontWeight: 700, color: healthColor(site.healthScore) }}>
                      {site.healthScore > 0 ? site.healthScore : "—"}
                    </span>
                  </td>
                  <td><StatusBadge status={site.status} /></td>
                  <td onClick={e => e.stopPropagation()}>
                    <div className="sm-action-row">
                      <button className="sm-icon-btn" title="View" onClick={() => onViewSite(site)}><FiChevronRight size={15} /></button>
                      <button className="sm-icon-btn" title="Edit" onClick={() => onEdit(site)}><FiEdit2 size={13} /></button>
                      <button className="sm-icon-btn sm-icon-red" title="Delete" onClick={() => onDelete(site)}><FiTrash2 size={13} /></button>
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
          {filtered.length === 0 && (
            <div className="sm-empty">No sites match your filters.</div>
          )}
        </div>
      )}

      {/* ── MAP VIEW ── */}
      {viewMode === "map" && (
        <div className="sm-map-view">
          <MapContainer center={[18.5, 73.9]} zoom={7} style={{ width: "100%", height: 520 }} zoomControl={true}>
            <TileLayer
              attribution='&copy; <a href="https://www.openstreetmap.org/copyright">OpenStreetMap</a>'
              url="https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png"
            />
            {filtered.map(site => (
              <Marker
                key={site.id}
                position={[site.lat, site.lng]}
                icon={makeSiteIcon(site, mapSelected === site.id)}
                eventHandlers={{ click: () => setMapSelected(site.id) }}
              >
                <Popup>
                  <div style={{ minWidth: 180, fontSize: 12 }}>
                    <div style={{ fontWeight: 700, fontSize: 13, marginBottom: 4 }}>{site.name}</div>
                    <div style={{ color: "#64748b", marginBottom: 2 }}>{site.type} · {site.plan}</div>
                    <div style={{ marginBottom: 2 }}>📍 {site.address.split(",").slice(-2).join(",").trim()}</div>
                    <div style={{ marginBottom: 2 }}>Health: <strong style={{ color: healthColor(site.healthScore) }}>{site.healthScore}</strong></div>
                    <div style={{ marginBottom: 6 }}>Devices: {site.devices.length} · Users: {site.users.length}</div>
                    <button onClick={() => onViewSite(site)} style={{ width: "100%", padding: "4px 8px", background: "#7c3aed", color: "white", border: "none", borderRadius: 6, cursor: "pointer", fontSize: 12 }}>
                      View Site →
                    </button>
                  </div>
                </Popup>
              </Marker>
            ))}
          </MapContainer>
          <div className="sm-map-legend">
            <span className="sm-legend-item"><span className="sm-legend-dot" style={{ background: "#10b981" }} />Healthy (≥85)</span>
            <span className="sm-legend-item"><span className="sm-legend-dot" style={{ background: "#f59e0b" }} />Warning (65–84)</span>
            <span className="sm-legend-item"><span className="sm-legend-dot" style={{ background: "#ef4444" }} />Critical (&lt;65)</span>
            <span className="sm-legend-item"><span className="sm-legend-dot" style={{ background: "#94a3b8" }} />Inactive</span>
          </div>
        </div>
      )}
    </div>
  );
};

// ═══════════════════════════════════════════════════════════════════════════════
//  SITE DASHBOARD DETAIL VIEW
// ═══════════════════════════════════════════════════════════════════════════════
const DASH_TABS = [
  { id: "overview",   label: "Overview",   icon: FiActivity  },
  { id: "devices",    label: "Devices",    icon: FiCpu       },
  { id: "solutions",  label: "Solutions",  icon: FiGrid      },
  { id: "users",      label: "Users",      icon: FiUsers     },
  { id: "alerts",     label: "Alerts",     icon: FiBell      },
  { id: "reports",    label: "Reports",    icon: FiFileText  },
  { id: "settings",   label: "Settings",   icon: FiSettings  },
];

/* ── Overview Tab ── */
const OverviewTab = ({ site }) => {
  const critCount = site.alerts.filter(a => a.severity === "Critical").length;
  const onlineDevices = site.devices.filter(d => d.status === "Online").length;
  const kpis = [
    { label: "Total Devices",     value: site.devices.length,   sub: `${onlineDevices} online`,        color: "#3b82f6" },
    { label: "Users",             value: site.users.length,     sub: "assigned",                       color: "#8b5cf6" },
    { label: "Active Alerts",     value: site.alerts.length,    sub: `${critCount} critical`,           color: site.alerts.length > 0 ? "#ef4444" : "#10b981" },
    { label: "Health Score",      value: site.healthScore || "—", sub: "site average",                 color: healthColor(site.healthScore) },
    { label: "Uptime",            value: site.uptime > 0 ? `${site.uptime}%` : "—", sub: "30 days",   color: "#10b981" },
    { label: "Solutions Active",  value: site.solutions.filter(s => s.enabled).length, sub: "enabled", color: "#f59e0b" },
  ];
  return (
    <div className="sm-overview">
      {/* KPI Strip */}
      <div className="sm-kpi-row">
        {kpis.map(k => (
          <div key={k.label} className="sm-kpi-card">
            <div className="sm-kpi-val" style={{ color: k.color }}>{k.value}</div>
            <div className="sm-kpi-label">{k.label}</div>
            <div className="sm-kpi-sub">{k.sub}</div>
          </div>
        ))}
      </div>

      {/* Two-column layout */}
      <div className="sm-overview-grid">
        {/* Recent Alerts */}
        <div className="sm-ov-card">
          <div className="sm-ov-card-title"><FiAlertTriangle size={14} /> Recent Alerts</div>
          {site.alerts.length === 0
            ? <div className="sm-empty-msg">✅ No active alerts</div>
            : site.alerts.map(a => (
              <div key={a.id} className="sm-ov-alert-row">
                <span className="sm-sev-dot" style={{ background: sevColor[a.severity] }} />
                <div style={{ flex: 1 }}>
                  <div style={{ fontWeight: 600, fontSize: 13 }}>{a.type}</div>
                  <div style={{ fontSize: 11, color: "#64748b" }}>{a.message.slice(0, 70)}…</div>
                </div>
                <span style={{ fontSize: 11, color: "#94a3b8" }}>{a.time}</span>
              </div>
            ))}
        </div>

        {/* Solutions Summary */}
        <div className="sm-ov-card">
          <div className="sm-ov-card-title"><FiGrid size={14} /> Solutions</div>
          {site.solutions.map(s => {
            const m = SOLUTION_META[s.id] || {};
            return (
              <div key={s.id} className="sm-ov-sol-row">
                <span className="sm-sol-icon" style={{ background: m.bg, color: m.color }}>{s.id[0]}</span>
                <div style={{ flex: 1 }}>
                  <div style={{ fontWeight: 600, fontSize: 13 }}>{s.name}</div>
                  <div style={{ fontSize: 11, color: "#94a3b8" }}>{s.plan} · {s.deviceCount} devices</div>
                </div>
                {s.activeAlerts > 0
                  ? <span className="sm-badge" style={{ background: "#fee2e2", color: "#991b1b" }}>{s.activeAlerts} alert{s.activeAlerts > 1 ? "s" : ""}</span>
                  : <span className="sm-badge" style={{ background: "#d1fae5", color: "#065f46" }}>Healthy</span>}
              </div>
            );
          })}
        </div>

        {/* Device Health */}
        <div className="sm-ov-card">
          <div className="sm-ov-card-title"><FiCpu size={14} /> Device Health Summary</div>
          {site.devices.slice(0, 6).map(d => (
            <div key={d.id} className="sm-ov-dev-row">
              <span className="sm-dev-status-dot" style={{ background: d.status === "Online" ? "#10b981" : d.status === "Offline" ? "#ef4444" : "#f59e0b" }} />
              <div style={{ flex: 1, fontSize: 12 }}>
                <span style={{ fontWeight: 600 }}>{d.name}</span>
                <span style={{ color: "#94a3b8", marginLeft: 6 }}>{d.lastSeen}</span>
              </div>
              <span style={{ fontSize: 12, fontWeight: 700, color: healthColor(d.health) }}>{d.health}%</span>
            </div>
          ))}
        </div>

        {/* Site Info */}
        <div className="sm-ov-card">
          <div className="sm-ov-card-title"><FiClipboard size={14} /> Site Information</div>
          {[
            ["Organization", site.org],
            ["Type",         site.type],
            ["Plan",         site.plan],
            ["Contact",      site.contact],
            ["Phone",        site.phone],
            ["Email",        site.email],
            ["Timezone",     site.timezone],
            ["Created",      site.createdAt],
          ].map(([k, v]) => (
            <div key={k} className="sm-info-row">
              <span className="sm-info-lbl">{k}</span>
              <span className="sm-info-val">{v}</span>
            </div>
          ))}
        </div>
      </div>
    </div>
  );
};

/* ── Devices Tab ── */
const DevicesTab = ({ site }) => {
  const [filter, setFilter] = useState("All");
  const statuses = ["All", "Online", "Offline", "Fault"];
  const filtered = filter === "All" ? site.devices : site.devices.filter(d => d.status === filter);
  return (
    <div>
      <div style={{ display: "flex", gap: 8, marginBottom: 12 }}>
        {statuses.map(s => (
          <button key={s} className={`sm-filter-chip ${filter === s ? "sm-chip-active" : ""}`} onClick={() => setFilter(s)}>
            {s} <span className="sm-filter-count">{s === "All" ? site.devices.length : site.devices.filter(d => d.status === s).length}</span>
          </button>
        ))}
      </div>
      <div className="sm-table-wrap">
        <table className="sm-table">
          <thead><tr>{["Device", "Type", "Connectivity", "Status", "Health", "Last Seen", "Actions"].map(h => <th key={h}>{h}</th>)}</tr></thead>
          <tbody>
            {filtered.map(d => (
              <tr key={d.id}>
                <td>
                  <div style={{ fontWeight: 600, fontSize: 13 }}>{d.name}</div>
                  <div style={{ fontSize: 11, color: "#94a3b8" }}>{d.id}</div>
                </td>
                <td><span className="sm-type-chip">{d.type}</span></td>
                <td style={{ fontSize: 12 }}>{d.connectivity}</td>
                <td>
                  <span className="sm-badge" style={{
                    background: d.status === "Online" ? "#d1fae5" : d.status === "Offline" ? "#fee2e2" : "#fef3c7",
                    color:      d.status === "Online" ? "#065f46" : d.status === "Offline" ? "#991b1b" : "#92400e",
                  }}>{d.status}</span>
                </td>
                <td style={{ fontWeight: 700, color: healthColor(d.health) }}>{d.health}%</td>
                <td style={{ fontSize: 12, color: "#64748b" }}>{d.lastSeen}</td>
                <td>
                  <div className="sm-action-row">
                    <button className="sm-icon-btn" title="View"><FiChevronRight size={14} /></button>
                    <button className="sm-icon-btn" title="Reassign"><FiEdit2 size={13} /></button>
                    <button className="sm-icon-btn sm-icon-red" title="Remove"><FiTrash2 size={13} /></button>
                  </div>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
      <div className="sm-bulk-bar">
        <span style={{ fontSize: 13, color: "#6b7280" }}>Bulk actions:</span>
        <button className="sm-btn sm-btn-ghost">Move Devices</button>
        <button className="sm-btn sm-btn-ghost">Update Firmware</button>
        <button className="sm-btn sm-btn-ghost sm-btn-red-ghost">Remove from Site</button>
      </div>
    </div>
  );
};

/* ── Solutions Tab ── */
const SolutionsTab = ({ site }) => (
  <div className="sm-sol-dash-grid">
    {site.solutions.map(s => {
      const m = SOLUTION_META[s.id] || {};
      return (
        <div key={s.id} className="sm-sol-dash-card" style={{ borderTop: `3px solid ${m.color}` }}>
          <div className="sm-sol-dash-header">
            <span className="sm-sol-icon lg" style={{ background: m.bg, color: m.color }}>{s.id[0]}</span>
            <div>
              <div style={{ fontWeight: 700, fontSize: 15 }}>{s.name}</div>
              <div style={{ fontSize: 11, color: "#94a3b8" }}>{s.plan} Plan</div>
            </div>
            <div className={`sm-toggle ${s.enabled ? "sm-toggle-on" : ""}`} style={{ marginLeft: "auto", background: s.enabled ? m.color : "#d1d5db" }}>
              <div className="sm-toggle-thumb" />
            </div>
          </div>
          <div className="sm-sol-dash-kpis">
            <div className="sm-sol-kpi"><div style={{ fontWeight: 700, color: "#1e293b" }}>{s.deviceCount}</div><div style={{ fontSize: 11, color: "#94a3b8" }}>Devices</div></div>
            <div className="sm-sol-kpi"><div style={{ fontWeight: 700, color: s.activeAlerts > 0 ? "#ef4444" : "#10b981" }}>{s.activeAlerts}</div><div style={{ fontSize: 11, color: "#94a3b8" }}>Alerts</div></div>
          </div>
          <div style={{ fontSize: 12, color: "#6b7280", marginBottom: 10 }}>
            Features: {s.features.join(", ")}
          </div>
          <div style={{ display: "flex", gap: 8 }}>
            <button className="sm-btn sm-btn-primary" style={{ flex: 1, fontSize: 11, padding: "5px" }}>View Dashboard</button>
            <button className="sm-btn sm-btn-ghost" style={{ fontSize: 11, padding: "5px 10px" }}>Disable</button>
          </div>
        </div>
      );
    })}
  </div>
);

/* ── Users Tab ── */
const UsersTab = ({ site }) => {
  const roleColors = { "Site Admin": { bg: "#ede9fe", color: "#5b21b6" }, "Facility Manager": { bg: "#dbeafe", color: "#1e40af" }, "Technician": { bg: "#d1fae5", color: "#065f46" }, "Viewer": { bg: "#f1f5f9", color: "#475569" } };
  return (
    <div>
      <div style={{ display: "flex", justifyContent: "flex-end", marginBottom: 12 }}>
        <button className="sm-btn sm-btn-primary"><FiPlus size={13} /> Add User</button>
      </div>
      <div className="sm-table-wrap">
        <table className="sm-table">
          <thead><tr>{["User", "Role", "Email", "Status", "Last Login", "Actions"].map(h => <th key={h}>{h}</th>)}</tr></thead>
          <tbody>
            {site.users.map(u => (
              <tr key={u.id}>
                <td>
                  <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
                    <div className="sm-user-avatar">{u.name.charAt(0)}</div>
                    <span style={{ fontWeight: 600, fontSize: 13 }}>{u.name}</span>
                  </div>
                </td>
                <td>
                  <span className="sm-badge" style={roleColors[u.role] || { bg: "#f1f5f9", color: "#475569" }}>
                    {u.role}
                  </span>
                </td>
                <td style={{ fontSize: 12, color: "#64748b" }}>{u.email}</td>
                <td><span className="sm-badge" style={{ background: u.status === "Active" ? "#d1fae5" : "#fee2e2", color: u.status === "Active" ? "#065f46" : "#991b1b" }}>{u.status}</span></td>
                <td style={{ fontSize: 12, color: "#64748b" }}>{u.lastLogin}</td>
                <td>
                  <div className="sm-action-row">
                    <button className="sm-icon-btn" title="Change Role"><FiEdit2 size={13} /></button>
                    <button className="sm-icon-btn sm-icon-red" title="Remove"><FiTrash2 size={13} /></button>
                  </div>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
};

/* ── Alerts Tab ── */
const AlertsTab = ({ site }) => {
  const [sevF, setSevF] = useState("All");
  const sevs = ["All", "Critical", "Warning", "Info"];
  const filtered = sevF === "All" ? site.alerts : site.alerts.filter(a => a.severity === sevF);
  return (
    <div>
      <div style={{ display: "flex", gap: 8, marginBottom: 12 }}>
        {sevs.map(s => (
          <button key={s} className={`sm-filter-chip ${sevF === s ? "sm-chip-active" : ""}`} onClick={() => setSevF(s)}>
            {s !== "All" && <span className="sm-sev-dot" style={{ background: sevColor[s] }} />}
            {s}
          </button>
        ))}
      </div>
      {filtered.length === 0
        ? <div className="sm-empty-msg">✅ No {sevF !== "All" ? sevF.toLowerCase() : ""} alerts for this site.</div>
        : filtered.map(a => (
          <div key={a.id} className="sm-alert-card" style={{ borderLeft: `4px solid ${sevColor[a.severity]}` }}>
            <div className="sm-alert-card-header">
              <span className="sm-badge" style={{ background: sevColor[a.severity] + "22", color: sevColor[a.severity] }}>{a.severity}</span>
              <span style={{ fontSize: 13, fontWeight: 600 }}>{a.type}</span>
              <span style={{ fontSize: 11, color: "#94a3b8", marginLeft: "auto" }}>{a.time}</span>
              <span className="sm-badge" style={{ background: a.status === "Active" ? "#fee2e2" : "#d1fae5", color: a.status === "Active" ? "#991b1b" : "#065f46" }}>{a.status}</span>
            </div>
            <div style={{ fontSize: 13, color: "#475569", margin: "6px 0" }}>{a.message}</div>
            <div style={{ display: "flex", gap: 8 }}>
              <button className="sm-btn sm-btn-ghost" style={{ fontSize: 11, padding: "4px 10px" }}>Acknowledge</button>
              <button className="sm-btn sm-btn-ghost" style={{ fontSize: 11, padding: "4px 10px" }}>Assign</button>
              <button className="sm-btn sm-btn-ghost" style={{ fontSize: 11, padding: "4px 10px" }}>Escalate</button>
              {a.assignee !== "—" && <span style={{ fontSize: 11, color: "#94a3b8", alignSelf: "center" }}>👤 {a.assignee}</span>}
            </div>
          </div>
        ))}
    </div>
  );
};

/* ── Reports Tab ── */
const ReportsTab = ({ site }) => (
  <div className="sm-reports">
    <div className="sm-report-kpi-row">
      {[
        { label: "Devices Monitored",     value: site.devices.length     },
        { label: "Total Alerts (30d)",    value: site.alerts.length * 12 },
        { label: "Alerts Resolved (30d)", value: site.alerts.length * 9  },
        { label: "Avg Response Time",     value: "24 min"                },
        { label: "Uptime (30d)",          value: site.uptime > 0 ? `${site.uptime}%` : "—" },
      ].map(k => (
        <div key={k.label} className="sm-kpi-card">
          <div className="sm-kpi-val" style={{ color: "#1e293b" }}>{k.value}</div>
          <div className="sm-kpi-label">{k.label}</div>
        </div>
      ))}
    </div>
    <div className="sm-report-actions">
      {["Daily Summary Report", "Weekly Analytics", "Compliance Report", "Device Health Report", "User Activity Report"].map(r => (
        <div key={r} className="sm-report-item">
          <FiFileText size={16} color="#7c3aed" />
          <span style={{ flex: 1, fontSize: 13 }}>{r}</span>
          <button className="sm-btn sm-btn-ghost" style={{ fontSize: 11, padding: "4px 10px" }}>Download PDF</button>
          <button className="sm-btn sm-btn-ghost" style={{ fontSize: 11, padding: "4px 10px" }}>Email</button>
        </div>
      ))}
    </div>
  </div>
);

/* ── Settings Tab ── */
const SettingsTab = ({ site, onEdit }) => (
  <div className="sm-settings-panel">
    <div className="sm-settings-section">
      <div className="sm-settings-title">Site Configuration</div>
      {[
        ["Site Name",    site.name],
        ["Organization", site.org],
        ["Type",         site.type],
        ["Plan",         site.plan],
        ["Status",       site.status],
        ["Address",      site.address],
        ["GPS",          `${site.lat}, ${site.lng}`],
        ["Contact",      site.contact],
        ["Phone",        site.phone],
        ["Email",        site.email],
      ].map(([k, v]) => (
        <div key={k} className="sm-info-row">
          <span className="sm-info-lbl">{k}</span>
          <span className="sm-info-val">{v}</span>
        </div>
      ))}
      <button className="sm-btn sm-btn-primary" style={{ marginTop: 12 }} onClick={onEdit}>
        <FiEdit2 size={13} /> Edit Site Info
      </button>
    </div>

    <div className="sm-settings-section">
      <div className="sm-settings-title">Alert & Escalation Rules</div>
      <div className="sm-info-row"><span className="sm-info-lbl">Escalation</span><span className="sm-info-val">{site.settings.escalation}</span></div>
      <div className="sm-info-row"><span className="sm-info-lbl">Maintenance Window</span><span className="sm-info-val">{site.settings.maintenanceWindow}</span></div>
      <div className="sm-info-row"><span className="sm-info-lbl">SLA Target</span><span className="sm-info-val">{site.settings.slaTarget}</span></div>
      <div className="sm-info-row"><span className="sm-info-lbl">Data Retention</span><span className="sm-info-val">{site.settings.dataRetention}</span></div>
      <div className="sm-info-row">
        <span className="sm-info-lbl">Notification Channels</span>
        <span className="sm-info-val">{site.settings.notificationChannels.join(", ") || "—"}</span>
      </div>
    </div>

    <div className="sm-settings-section sm-settings-danger">
      <div className="sm-settings-title" style={{ color: "#ef4444" }}>Danger Zone</div>
      <div className="sm-danger-row">
        <div>
          <div style={{ fontWeight: 600, fontSize: 13 }}>Deactivate Site</div>
          <div style={{ fontSize: 12, color: "#6b7280" }}>Site goes offline, devices retained. Can be reactivated.</div>
        </div>
        <button className="sm-btn sm-btn-amber"><FiPower size={13} /> Deactivate</button>
      </div>
      <div className="sm-danger-row">
        <div>
          <div style={{ fontWeight: 600, fontSize: 13 }}>Archive Site</div>
          <div style={{ fontSize: 12, color: "#6b7280" }}>Move to archive. Data retained for 6 months.</div>
        </div>
        <button className="sm-btn sm-btn-ghost">Archive</button>
      </div>
      <div className="sm-danger-row">
        <div>
          <div style={{ fontWeight: 600, fontSize: 13 }}>Delete Site</div>
          <div style={{ fontSize: 12, color: "#6b7280" }}>Permanent. Requires all devices removed first.</div>
        </div>
        <button className="sm-btn sm-btn-red" disabled={site.devices.length > 0} style={{ opacity: site.devices.length > 0 ? 0.4 : 1 }}>
          <FiTrash2 size={13} /> Delete
        </button>
      </div>
    </div>
  </div>
);

/* ── Site Dashboard Root ── */
const SiteDashboardView = ({ site, onBack, onEdit }) => {
  const [activeTab, setActiveTab] = useState("overview");
  const critAlerts = site.alerts.filter(a => a.severity === "Critical").length;

  return (
    <div className="sm-container">
      {/* Breadcrumb */}
      <button className="sm-back-btn" onClick={onBack}><FiChevronLeft size={15} /> All Sites</button>

      {/* Site Header */}
      <div className="sm-dash-header">
        <div className="sm-dash-site-avatar" style={{ background: healthColor(site.healthScore) }}>
          {site.name.charAt(0)}
        </div>
        <div className="sm-dash-header-info">
          <div className="sm-dash-site-name">{site.name}</div>
          <div className="sm-dash-site-meta">
            <span className="sm-type-chip">{site.type}</span>
            <StatusBadge status={site.status} />
            <span style={{ fontSize: 12, color: "#6b7280" }}><FiMapPin size={11} /> {site.address.split(",").slice(-2).join(",").trim()}</span>
            <span style={{ fontSize: 12, color: "#6b7280" }}>Plan: <strong>{site.plan}</strong></span>
          </div>
        </div>
        <div className="sm-dash-health-ring">
          <div className="sm-dash-health-val" style={{ color: healthColor(site.healthScore) }}>
            {site.healthScore > 0 ? site.healthScore : "—"}
          </div>
          <div className="sm-dash-health-lbl">Health</div>
        </div>
        <div className="sm-dash-header-actions">
          <button className="sm-btn sm-btn-primary" onClick={onEdit}><FiEdit2 size={13} /> Edit Site</button>
          <button className="sm-btn sm-btn-amber"><FiPower size={13} /> Deactivate</button>
        </div>
      </div>

      {/* Tab Bar */}
      <div className="sm-tab-bar">
        {DASH_TABS.map(tab => (
          <button
            key={tab.id}
            className={`sm-tab ${activeTab === tab.id ? "sm-tab-active" : ""}`}
            onClick={() => setActiveTab(tab.id)}
          >
            <tab.icon size={14} />
            {tab.label}
            {tab.id === "alerts" && critAlerts > 0 && <span className="sm-tab-badge">{critAlerts}</span>}
          </button>
        ))}
      </div>

      {/* Tab Content */}
      <div className="sm-dash-panel">
        {activeTab === "overview"  && <OverviewTab  site={site} />}
        {activeTab === "devices"   && <DevicesTab   site={site} />}
        {activeTab === "solutions" && <SolutionsTab site={site} />}
        {activeTab === "users"     && <UsersTab     site={site} />}
        {activeTab === "alerts"    && <AlertsTab    site={site} />}
        {activeTab === "reports"   && <ReportsTab   site={site} />}
        {activeTab === "settings"  && <SettingsTab  site={site} onEdit={onEdit} />}
      </div>
    </div>
  );
};

// ═══════════════════════════════════════════════════════════════════════════════
//  ROOT COMPONENT
// ═══════════════════════════════════════════════════════════════════════════════
const SiteManagement = () => {
  const [view,         setView]         = useState("list");   // "list" | "detail"
  const [selectedSite, setSelectedSite] = useState(null);
  const [showCreate,   setShowCreate]   = useState(false);
  const [editingSite,  setEditingSite]  = useState(null);
  const [deleteTarget, setDeleteTarget] = useState(null);

  const handleViewSite = (site) => { setSelectedSite(site); setView("detail"); };
  const handleEdit     = (site) => { setEditingSite(site); setShowCreate(true); };
  const handleDelete   = (site) => { setDeleteTarget(site); };
  const handleDeleteConfirm = (action) => { console.log(`${action} site:`, deleteTarget?.id); setDeleteTarget(null); };

  return (
    <>
      {view === "list"
        ? <SiteListView onViewSite={handleViewSite} onEdit={handleEdit} onDelete={handleDelete} onCreateSite={() => { setEditingSite(null); setShowCreate(true); }} />
        : <SiteDashboardView site={selectedSite} onBack={() => setView("list")} onEdit={() => handleEdit(selectedSite)} />}

      {showCreate   && <CreateSiteModal editSite={editingSite} onClose={() => { setShowCreate(false); setEditingSite(null); }} />}
      {deleteTarget && <DeleteDialog site={deleteTarget} onClose={() => setDeleteTarget(null)} onConfirm={handleDeleteConfirm} />}
    </>
  );
};

export default SiteManagement;
